<template>
  <div>
    <fieldset v-if="language === currentLanguage">
      <legend class="visually-hidden">{{ $t('resource.basicInformation') }}</legend>

      <v-form-input
        id="supplier-resource-create-name"
        v-model="nameLocal"
        :label="`${$t('general.shared.name')}*`"
        :yup-errors-variable="errors.nameLocal"
        autocomplete="off"
        form-type="outline"
        @input="$emit('update:name', nameLocal)"
      />

      <div class="form-outline">
        <label class="label">
          <span>{{ $t('general.shared.description') }}</span>
        </label>

        <v-html-editor
          id="supplier-resource-create-description"
          ref="supplier-resource-create-description-ref"
          :disabled="disabledFields"
          :value="descriptionLocal"
          @input="descriptionLocal = $event; $emit('update:description', descriptionLocal)"
          @media-uploaded="$emit('mediaUploaded', $event)"
        />
      </div>
    </fieldset>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import * as yup from 'yup';
import { useField } from 'vee-validate';
import { VFormInput } from '@uniqoders/form';
import { useI18n } from 'vue-i18n';
import useFormValidation from '@/helpers/form';
import useConcepts from '@/helpers/Concepts';
import VHtmlEditor from '@/components/vendor/htmlEditor/VHtmlEditor.vue';

export default defineComponent({
  name: 'TheSupplierResourceCreateBasicInformationFieldset',
  components: {
    VFormInput,
    VHtmlEditor,
  },
  emits: [
    'update:name',
    'update:description',
    'isValid',
    'mediaUploaded',
  ],
  props: {
    name: {
      type: String,
      required: false,
      default: '',
    },
    description: {
      type: String,
      required: false,
      default: '',
    },
    disabledFields: {
      type: Boolean,
      required: false,
      default: false,
    },
    language: {
      type: String,
      required: true,
      default: '',
    },
    currentLanguage: {
      type: String,
      required: true,
      default: '',
    },
    clear: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useI18n();

    const rules = yup.object({
      nameLocal: yup.string()
        .required()
        .label(t('general.shared.name')),
      descriptionLocal: yup.string()
        .nullable()
        .label(t('general.shared.description')),
    });

    const initialValues = {
      nameLocal: props.name,
      descriptionLocal: props.description,
    };

    const form = useFormValidation(rules, initialValues);

    const { value: nameLocal } = useField('nameLocal');
    const { value: descriptionLocal } = useField('descriptionLocal');

    return {
      ...useConcepts(),
      ...form,
      nameLocal,
      descriptionLocal,
    };
  },
  computed: {
    isValid() {
      return this.meta.valid && this.meta.dirty;
    },
  },
  watch: {
    name(value) {
      this.nameLocal = value;
    },
    description(value) {
      this.descriptionLocal = value;
    },
    clear() {
      this.resetForm();
    },
    isValid(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('isValid', newValue);
      }
    },
  },
});
</script>
